import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { DecimalPipe, Location } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AdminService } from '../../../admin/admin.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { GridModule, ExcelModule } from '@progress/kendo-angular-grid';
import { Unit } from '../../../models/admin-models';
import { MatDialog } from '@angular/material/dialog';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { DashboardService } from '../../../dashboard/dashboard.service';
import { DateHandlingService } from '../../../services/date-handling.service';
import { EnvironmentService } from '../../../services/environment.service';
import { UniversalSearchService } from '../../../universal-search/services/universal-search.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { SessionStorageService } from '../../../services/session-storage.service';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { UserManagementService } from '../../../admin/user-management/user-management.service';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { NgxLoadingModule } from 'ngx-loading';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LocalTime } from '../../../local-time.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-unit-usage',
  templateUrl: './unit-usage.component.html',
  styleUrls: ['./unit-usage.component.scss'],
  imports: [
    NgxLoadingModule,
    MatSelectModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    GridModule,
    MatIconModule,
    MatChipsModule,
    MatAutocompleteModule,
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
    DecimalPipe,
    LocalTime,
    ExcelModule,
    MatButtonModule,
    MatInputModule,
  ],
})
export class UnitUsageComponent implements OnInit, OnDestroy {
  private admnSvc = inject(AdminService);
  private dateSvc = inject(DateHandlingService);
  private dbs = inject(DashboardService);
  dialog = inject(MatDialog);
  private envSvc = inject(EnvironmentService);
  private pltfrm = inject(Platform);
  private snacky = inject(MatSnackBar);
  private ss = inject(SessionStorageService);
  private userService = inject(UserManagementService);
  private usServ = inject(UniversalSearchService);
  private location = inject(Location);

  private environment;
  private baseUrl: string;
  usageUnitsControl = new FormControl();
  usage = { start: null, end: null };
  usageGridData = { headers: [], data: [], total: 0 };
  dateFormatted = ['Session Start', 'Session End', 'Date'];
  reportTypes = [
    { display: 'Aggregated User Report', value: 'aggregated' },
    { display: 'Raw Session Details', value: 'details' },
  ];
  reportType = '';
  filteredUnits;
  units: Unit[] = [] as Unit[];
  loading = false;
  fileLoading = false;
  skip = 0;
  take = 25;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  users = [];
  filteredUsers = [];
  userCtrl = new FormControl();
  removable = true;
  unsubscribe = new Subject();

  @ViewChild('userInput', { static: false })
  userInput: ElementRef<HTMLInputElement>;
  noResults: boolean = false;

  ngOnInit(): void {
    this.getUsageUnits();
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    if (this.ss.get('rights').includes('aiNoteUserUsage')) {
      this.reportTypes.push({
        display: 'Note Assistant Details',
        value: 'assistant',
      });
    }
    if (this.ss.get('rights').includes('BookManagement')) {
      this.reportTypes.push(
        {
          display: 'Book Management - Aggregated Usage',
          value: 'bm-aggregated',
        },
        { display: 'Book Management - Session Details', value: 'bm-details' },
        { display: 'Book Management - Policies Viewed', value: 'bm-viewed' }
      );
    }

    this.userCtrl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        if (value && value.length >= 1) {
          this.userLookAhead(value);
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getUsageUnits() {
    this.usageUnitsControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value) {
          this.admnSvc.getUnitList(value, true).subscribe(resp => {
            if (resp['names']) {
              this.filteredUnits = resp['names'];
            }
          });
        }
      });
  }

  checkSelection() {
    if (
      !this.filteredUnits ||
      !this.filteredUnits.find(unit => {
        return unit.id === this.usageUnitsControl.value;
      })
    ) {
      this.usageUnitsControl.setValue(null);
    }
  }

  autoCompleteUnitChanged(ev) {
    this.usageUnitsControl.setValue(ev.option.value);
  }

  loadHeaders(dataSet) {
    this.usageGridData.headers = [];
    const columns = Object.keys(dataSet[0]);
    columns.map(header => {
      this.usageGridData.headers.push({
        DataField: header,
        Hidden: true,
        Label: header,
        Sort: false,
        Filter: false,
        Type: 'string',
      });
    });
  }

  getUsage() {
    this.loading = true;
    this.noResults = false;
    this.usageGridData.data = [];
    this.usageGridData.total = 0;
    const reportParams: {
      take: number | string;
      skip: number | string;
      search: any;
      sort: any;
      group?: string | null;
      name?: string | null;
    } = {
      take: this.take,
      skip: this.skip,
      search: {
        savedTime: {
          start: this.dateSvc.toUtcIsoString(this.usage.start, 'start'),
          end: this.dateSvc.toUtcIsoString(this.usage.end, 'end'),
        },
      },
      sort: {},
      group: this.reportType,
      name: 'Unit Usage Report',
    };

    if (this.usageUnitsControl.value) {
      reportParams.search['unitId'] = { equals: this.usageUnitsControl.value };
    }

    if (this.users.length) {
      reportParams.search['userIds'] = {
        selected: this.users.map(
          (userVal: { id: string; name: string }) => userVal.id
        ),
      };
    }

    if (this.reportType === 'assistant') {
      reportParams.name = 'Note Assistant Details';
      delete reportParams.group;
      delete reportParams.sort;
    }

    if (this.reportType.includes('bm-')) {
      const reportName = this.reportTypes.find(
        report => report.value === this.reportType
      );

      delete reportParams.group;

      if (reportName) {
        reportParams.name = reportName.display;
      }
    }

    this.dbs.getDynamicReport(reportParams).subscribe({
      next: resp => {
        if (resp && resp['data']?.length) {
          this.loadHeaders(resp['data']);
          this.usageGridData.data = resp['data'];
          this.usageGridData.total = resp['total'] || resp['data'].length;
          this.noResults = false;
        } else {
          this.noResults = true;
        }
        this.loading = false;
      },
      error: err => {
        console.warn('Error: ', err);
        this.snacky.open(
          `Oops! Something went wrong. Please try again later.`,
          'Close',
          {
            duration: 3000,
          }
        );
        this.loading = false;
      },
    });
  }

  dataStateChange(ev) {
    this.loading = true;
    this.take = ev.take;
    this.skip = ev.skip;
    this.getUsage();
  }

  exportToExcel(): void {
    const reportParams = {
      take: 999999999,
      skip: 0,
      search: {
        savedTime: {
          start: this.dateSvc.toUtcIsoString(this.usage.start, 'start'),
          end: this.dateSvc.toUtcIsoString(this.usage.end, 'end'),
        },
      },
      sort: {},
      group: this.reportType,
      name: 'Unit Usage Report',
    };

    if (this.usageUnitsControl.value) {
      reportParams.search['unitId'] = {
        equals: this.usageUnitsControl.value,
      };
    }

    if (this.users.length) {
      reportParams.search['userId'] = {
        selected: this.users.map(
          (userVal: { id: string; name: string }) => userVal.id
        ),
      };
    }

    if (this.reportType.includes('bm-')) {
      const reportName = this.reportTypes.find(
        report => report.value === this.reportType
      );

      delete reportParams.group;

      if (reportName) {
        reportParams.name = reportName.display;
      }
    }

    let dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Disclosure',
        aiDisclosure: true,
        confirm: 'Agree and Download',
        cancel: 'Cancel/Exit',
        hideCancel: false,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.fileLoading = true;
        this.snacky.open(`File downloading. Please wait.`, 'Close', {
          duration: 4000,
        });
        this.dbs.getDynamicReportXlsx(reportParams).subscribe({
          next: data => {
            if (data) {
              const date = new Date();
              const filename = 'RightBridge-UnitUsageReport' + '.xlsx';
              const iosSafari =
                this.pltfrm.IOS ||
                (navigator?.['userAgentData']?.platform === 'macOS' &&
                  navigator.maxTouchPoints > 1);

              let fileData;
              const reader = new FileReader();
              reader.readAsDataURL(data);
              reader.onloadend = () => {
                fileData = reader.result;
                saveAs(fileData, filename, {
                  proxyURL: `${this.baseUrl}/util/proxy`,
                  proxyTarget: '_self',
                  forceProxy: iosSafari,
                });
                this.fileLoading = false;
                this.snacky.open(`Download complete.`, 'Close', {
                  duration: 4000,
                });
              };
            } else {
              this.fileLoading = false;
            }
          },
          error: () => {
            this.fileLoading = false;
            this.snacky.open(`An unexpected error was encountered.`, 'Close', {
              duration: 4000,
            });
          },
        });
      }
      dialogRef = null;
    });
  }

  userLookAhead(value: string) {
    this.usServ.userSearch(value).subscribe(resp => {
      if (resp.names) {
        this.filteredUsers = resp.names;
      }
    });
  }

  addItem(
    event: MatChipInputEvent,
    items: Array<any>,
    control: FormControl
  ): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      items.push(value.trim());
    }

    if (input) {
      input.value = '';
    }

    control.setValue(null);
  }

  removeItem(user: string, users: Array<any>): void {
    const index = users.indexOf(user);

    if (index >= 0) {
      users.splice(index, 1);
    }
  }

  selectedUser(event: MatAutocompleteSelectedEvent): void {
    this.users.push(event.option.value);
    this.userInput.nativeElement.value = '';
    this.userCtrl.setValue(null);
  }

  reportTypeChange(ev) {
    if (this.reportType !== 'assistant') {
      this.users = [];
    }
  }

  clearUnitField(ev) {
    this.usageUnitsControl.setValue(null);
  }

  viewPolicy(policyId: string) {
    window.open(
      `${this.location.prepareExternalUrl('')}book-management/${policyId}`,
      '_blank'
    );
  }
}
